export const EAGER = [
  '@paldesk/module-federation/core',
  '@paldesk/module-federation/remotes-registry',
];

const SINGLETON_ANGULAR = [
  '@angular/core',
  '@angular/common',
  '@angular/router',
  '@angular/platform-browser',
  '@angular/platform-browser-dynamic',
];

const SINGLETON_PACKAGES = ['zone.js', 'rxjs', '@ngrx', '@module-federation'];

/**
 * Provided-version for shared *workspace* libraries.
 *
 * Workspace libs have no package version, so webpack registers them under the
 * `ProvideSharedModule` default of `'0'`. The share scope is keyed by version
 * and `setShared` keeps the *first* registration for a given key — so host and
 * remote collide on the same `'0'` slot and whichever registers first provides
 * the module. That order follows chunk-load order, i.e. how the app happens to
 * be split, which is why identical code works in one app and breaks in a
 * bigger one.
 *
 * Declaring a version host-side and `'0'` remote-side puts them in separate
 * slots, and `findVersion` supersedes `'0'` unconditionally
 * (`if (prev === '0') return cur`). The host's copy then always provides,
 * regardless of registration order — which is the intended contract: the host
 * owns the singletons and remotes consume them.
 *
 * The value never needs bumping. It only has to differ from `'0'`.
 */
export const HOST_SHARED_LIB_VERSION = '1.0.0';
export const REMOTE_SHARED_LIB_VERSION = '0';

/**
 * The workspace libs that take {@link HOST_SHARED_LIB_VERSION}.
 *
 * Only DI contract libs belong here — libs a host publishes tokens through and
 * may add exports to over time. Those are the ones where an older remote's copy
 * winning the share scope is a real failure: the export simply isn't there and
 * `inject()` gets `undefined`.
 *
 * Not every entry in {@link SINGLETON_LIBS} qualifies, and adding one from
 * {@link EAGER} is actively harmful — `_createSharedConfig` ignores eager
 * modules here regardless, because versioning them produces two live copies
 * instead of one agreed copy. See the comment there for why that duplicates
 * Angular.
 */
export const VERSIONED_SHARED_LIBS = [
  '@core-features/core',
  '@core-features/bridge',
];

/**
 * Shared singletons that are workspace libraries.
 */
export const SINGLETON_LIBS = [
  '@core-features/core',
  '@core-features/bridge',
  '@paldesk/module-federation/core',
  '@paldesk/module-federation/shared',
  '@paldesk/module-federation/header',
  '@utils/interceptors',
];

export const REMOTE_SINGLETON = [
  ...SINGLETON_ANGULAR,
  ...SINGLETON_PACKAGES,
  ...SINGLETON_LIBS,
];

export const HOST_SINGLETON = [
  ...SINGLETON_ANGULAR,
  ...SINGLETON_PACKAGES,
  ...SINGLETON_LIBS,
];

export const PALDESK_HOST_FEDERATION_RUNTIME =
  '__paldesk_host_federation_runtime__';

export const PALDESK_HOST_CONFIG = '__paldesk_host_config__';
export const PALDESK_REMOTE_CONFIGS = '__paldesk_remote_configs__';
